import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store';
import {
  setCurrentProject,
  addRecentProject,
} from '@/store/slices/projectSlice';
import type { Project, File } from '@phrasewright/types';
import { SAMPLE_CONTENT } from './SampleContentCreator';

const ONBOARDING_STORAGE_KEY = 'phrasewright:onboarding-completed';
const LAST_PROJECT_KEY = 'phrasewright:lastProjectId';
const RECENT_PROJECTS_KEY = 'phrasewright:recentProjects';

export function useOnboarding() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const dispatch = useDispatch();
  const { currentProject, recentProjects } = useSelector(
    (state: RootState) => state.project
  );

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        // Check if onboarding was completed
        const onboardingCompleted = localStorage.getItem(
          ONBOARDING_STORAGE_KEY
        );
        const lastProjectId = localStorage.getItem(LAST_PROJECT_KEY);

        if (!onboardingCompleted || !lastProjectId) {
          // Show onboarding for new users or if no project was set
          setShowOnboarding(true);
          setIsInitializing(false);
          return;
        }

        // Try to restore the last project
        const storedRecentProjects = localStorage.getItem(RECENT_PROJECTS_KEY);
        if (storedRecentProjects) {
          const projects: Project[] = JSON.parse(storedRecentProjects);
          const lastProject = projects.find((p) => p.id === lastProjectId);

          if (lastProject) {
            // Restore project state
            dispatch(setCurrentProject(lastProject));
            projects.forEach((project) => dispatch(addRecentProject(project)));
          } else {
            // Project not found, show onboarding
            setShowOnboarding(true);
          }
        } else {
          // No recent projects, show onboarding
          setShowOnboarding(true);
        }
      } catch (error) {
        setShowOnboarding(true);
      } finally {
        setIsInitializing(false);
      }
    };

    checkOnboardingStatus();
  }, [dispatch]);

  const completeOnboarding = (project: Project) => {
    try {
      // Mark onboarding as completed
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
      localStorage.setItem(LAST_PROJECT_KEY, project.id);

      // Store project in recent projects
      const recentProjectsList = [project];
      localStorage.setItem(
        RECENT_PROJECTS_KEY,
        JSON.stringify(recentProjectsList)
      );

      // Update Redux state
      dispatch(setCurrentProject(project));
      dispatch(addRecentProject(project));

      setShowOnboarding(false);
    } catch (error) {}
  };

  const resetOnboarding = () => {
    try {
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      localStorage.removeItem(LAST_PROJECT_KEY);
      localStorage.removeItem(RECENT_PROJECTS_KEY);

      dispatch(setCurrentProject(null));
      setShowOnboarding(true);
    } catch (error) {}
  };

  return {
    showOnboarding,
    isInitializing,
    currentProject,
    recentProjects,
    completeOnboarding,
    resetOnboarding,
    setShowOnboarding,
  };
}

export async function createSampleContent(projectId: string): Promise<File[]> {
  const sampleFiles: File[] = [];

  for (const [path, content] of Object.entries(SAMPLE_CONTENT)) {
    const fileName = path.split('/').pop()!;
    const isPhrase = path.startsWith('phrases/');

    // Extract frontmatter to get metadata
    const frontmatterMatch = content.match(/^---\n([\s\S]*?)\n---\n/);
    const frontmatterData: any = {};

    if (frontmatterMatch) {
      const lines = frontmatterMatch[1].split('\n');
      for (const line of lines) {
        const colonIndex = line.indexOf(':');
        if (colonIndex > -1) {
          const key = line.substring(0, colonIndex).trim();
          let value: any = line.substring(colonIndex + 1).trim();

          // Parse arrays
          if (value.startsWith('[') && value.endsWith(']')) {
            try {
              value = JSON.parse(value.replace(/'/g, '"'));
            } catch {
              value = value
                .slice(1, -1)
                .split(',')
                .map((s: string) => s.trim().replace(/'/g, ''));
            }
          }

          frontmatterData[key] = value;
        }
      }
    }

    const file: File = {
      id: crypto.randomUUID(),
      name: fileName,
      path: path,
      content: content,
      metadata: {
        title:
          frontmatterData.title || fileName.replace(/\.(md|markdown)$/, ''),
        type: isPhrase ? 'phrase' : 'document',
        tags: frontmatterData.tags || [],
        custom: {
          category: frontmatterData.category,
          keywords: frontmatterData.keywords,
          usageCount: isPhrase ? 0 : undefined,
        },
      },
      revisions: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'active',
      projectId,
    };

    // Create initial revision
    file.revisions.push({
      id: crypto.randomUUID(),
      filePath: path,
      timestamp: file.createdAt,
      content: content,
      status: 'current',
      size: content.length,
    });

    sampleFiles.push(file);
  }

  return sampleFiles;
}
